class CustomCarousel extends HTMLElement {
	constructor() {
		super();
		this.splide = null;
		this.initialRowsToShow = 2; // Show first 2 rows initially
		this.rowsPerLoad = 2; // Load 2 more rows each time
		this.currentlyShownRows = 0;
		this.totalRows = 0;
	}

	connectedCallback() {
		this.filterScheduledItems();

		const enableDesktopCarousel = this.getAttribute('enable-desktop-carousel') === 'true';
		const enableMobileCarousel = this.getAttribute('enable-mobile-carousel') === 'true';

		// Get grid settings from attributes
		const desktopColumns = parseInt(this.getAttribute('desktop-columns')) || 3;
		const mobileColumns = parseInt(this.getAttribute('mobile-columns')) || 2;
		const desktopGap = parseInt(this.getAttribute('desktop-gap')) || 0;
		const mobileGap = parseInt(this.getAttribute('mobile-gap')) || 0;

		// Add new attribute parsing for device-specific settings
		const desktopAutoplay = this.getAttribute('desktop-autoplay') === 'true';
		const mobileAutoplay = this.getAttribute('mobile-autoplay') === 'true';
		const desktopInterval = parseInt(this.getAttribute('desktop-interval')) || 5000;
		const mobileInterval = parseInt(this.getAttribute('mobile-interval')) || 5000;
		const desktopInfiniteLoop = this.getAttribute('desktop-infinite-loop') !== 'false';
		const mobileInfiniteLoop = this.getAttribute('mobile-infinite-loop') !== 'false';
		const desktopDraggable = this.getAttribute('desktop-draggable') !== 'false';
		const mobileDraggable = this.getAttribute('mobile-draggable') !== 'false';
		const carouselType = this.getAttribute('carousel-type') || 'slide';

		const showArrows = this.getAttribute('show-arrows') !== 'false';
		const showDots = this.getAttribute('show-dots') !== 'false';
		const perMove = parseInt(this.getAttribute('per-move')) || 1;

		// Add new attribute parsing for view more functionality
		this.enableViewMore = this.getAttribute('enable-view-more') !== 'false';

		// Initialize Splide based on screen size and settings
		const isMobile = window.innerWidth < 750; // Match Shopify's breakpoint

		if ((isMobile && enableMobileCarousel) || (!isMobile && enableDesktopCarousel)) {
			this.initSplide({
				perPage: isMobile ? mobileColumns : desktopColumns,
				gap: `${isMobile ? mobileGap : desktopGap}px`,
				autoplay: isMobile ? mobileAutoplay : desktopAutoplay,
				interval: isMobile ? mobileInterval : desktopInterval,
				arrows: showArrows,
				pagination: showDots,
				perMove: perMove,
				type: (isMobile ? mobileInfiniteLoop : desktopInfiniteLoop) ? 'loop' : carouselType,
				drag: isMobile ? mobileDraggable : desktopDraggable
			});
		} else {
			// Remove Splide classes if carousel is not enabled for current device
			this.classList.remove('splide');
			const track = this.querySelector('.splide__track');
			const list = this.querySelector('.splide__list');

			if (track && list) {
				// Unwrap the track while preserving the list and its contents
				track.parentNode.insertBefore(list, track);
				track.parentNode.removeChild(track);

				// Remove Splide classes from list and slides
				list.classList.remove('splide__list');
				list.querySelectorAll('.splide__slide').forEach((slide) => {
					slide.classList.remove('splide__slide');
				});
				
				// Setup grid view with "View More" functionality
				this.setupGridView(list, isMobile ? mobileColumns : desktopColumns);
			}
		}

		// Handle resize events to reinitialize when needed
		window.addEventListener('resize', this.handleResize.bind(this));
	}
	
	setupGridView(list, columnsPerRow) {
		// Get all product items
		const items = Array.from(list.children);
		if (items.length === 0) return;
		
		// Calculate total rows
		this.totalRows = Math.ceil(items.length / columnsPerRow);
		this.currentlyShownRows = this.initialRowsToShow;
		
		// Get the "View All" link from the parent container if it exists
		const viewAllContainer = list.closest('.custom-tab-content')?.querySelector('.product-tabs-content-bottom');
		this.viewAllContainer = viewAllContainer;
		
		// If view more is disabled or total rows <= initial rows to show, show all items and view all button
		if (!this.enableViewMore || this.totalRows <= this.initialRowsToShow) {
			// No need to modify the DOM in this case
			if (viewAllContainer) {
				viewAllContainer.style.display = 'flex';
			}
			return;
		}
		
		// Clone all items to keep a reference
		this.allItems = items.map(item => item.cloneNode(true));
		this.list = list;
		this.columnsPerRow = columnsPerRow;
		
		// Keep only the initial rows visible
		items.forEach((item, index) => {
			const row = Math.floor(index / columnsPerRow);
			if (row >= this.initialRowsToShow) {
				item.remove(); // Remove from DOM
			}
		});
		
		// Create container for buttons
		const buttonContainer = document.createElement('div');
		buttonContainer.className = 'grid-view-buttons';
		buttonContainer.style.textAlign = 'center';
		buttonContainer.style.marginTop = '20px';
		list.parentNode.insertBefore(buttonContainer, list.nextSibling);
		
		// Create "View More" button if needed
		if (this.totalRows > this.initialRowsToShow) {
			this.createViewMoreButton(buttonContainer);
			
			// Hide the View All button initially
			if (viewAllContainer) {
				viewAllContainer.style.display = 'none';
			}
		}
	}
	
	createViewMoreButton(container) {
		const viewMoreBtn = document.createElement('button');
		viewMoreBtn.className = 'view-more-button';
		viewMoreBtn.textContent = 'View More';
		viewMoreBtn.style.border = '1px solid #0000008b';
		viewMoreBtn.style.padding = '5px 12px';
		viewMoreBtn.style.textTransform = 'uppercase';
		viewMoreBtn.style.fontSize = '12px';
		viewMoreBtn.style.letterSpacing = '1.3px';
		viewMoreBtn.style.transition = 'all 0.3s ease';
		viewMoreBtn.style.backgroundColor = 'transparent';
		viewMoreBtn.style.cursor = 'pointer';
		viewMoreBtn.style.margin = '0 auto';
		
		viewMoreBtn.addEventListener('mouseover', () => {
			viewMoreBtn.style.backgroundColor = '#0000008b';
			viewMoreBtn.style.color = '#fff';
		});
		
		viewMoreBtn.addEventListener('mouseout', () => {
			viewMoreBtn.style.backgroundColor = 'transparent';
			viewMoreBtn.style.color = '';
		});
		
		viewMoreBtn.addEventListener('click', () => {
			// Show loading state
			viewMoreBtn.textContent = 'Loading...';
			viewMoreBtn.disabled = true;
			
			// Use setTimeout to simulate lazy loading and allow the UI to update
			setTimeout(() => {
				// Calculate how many more items to show
				const nextRowsToShow = Math.min(this.currentlyShownRows + this.rowsPerLoad, this.totalRows);
				
				// Add the next batch of items to the DOM
				for (let i = this.currentlyShownRows * this.columnsPerRow; i < nextRowsToShow * this.columnsPerRow && i < this.allItems.length; i++) {
					const itemToAdd = this.allItems[i].cloneNode(true);
					this.lazyLoadImagesInElement(itemToAdd);
					this.list.appendChild(itemToAdd);
				}
				
				this.currentlyShownRows = nextRowsToShow;

				//Swym Dispatch event
				document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
				
				// If all rows are now shown, replace with View All button
				if (this.currentlyShownRows >= this.totalRows) {
					viewMoreBtn.remove();
					if (this.viewAllContainer) {
						this.viewAllContainer.style.display = 'flex';
					}
				} else {
					// Reset button state
					viewMoreBtn.textContent = 'View More';
					viewMoreBtn.disabled = false;
				}
			}, 300); // Small delay to simulate loading
		});
		
		container.appendChild(viewMoreBtn);
	}

	filterScheduledItems() {
		const items = this.querySelectorAll('.image-link-item');
		const now = new Date();
		
		items.forEach(item => {
			const enableSchedule = item.getAttribute('data-schedule-enabled') === 'true';
			if (!enableSchedule) return;

			const startDateStr = item.getAttribute('data-schedule-start-date');
			const endDateStr = item.getAttribute('data-schedule-end-date');
			const startTimeStr = item.getAttribute('data-schedule-start-time');
			const endTimeStr = item.getAttribute('data-schedule-end-time');

			if (!this.isItemVisible(now, startDateStr, endDateStr, startTimeStr, endTimeStr)) {
				item.remove();
			} else {
				item.style.display = '';
			}
		});
	}

	isItemVisible(now, startDateStr, endDateStr, startTimeStr, endTimeStr) {
		const parseDate = (dateStr) => {
			if (!dateStr) return null;
			const parts = dateStr.split('-');
			if (parts.length !== 3) return null;
			if (parts[0].length === 4) {
				return new Date(parts[0], parts[1] - 1, parts[2]);
			}
			return new Date(parts[2], parts[1] - 1, parts[0]);
		};
		
		const parseTime = (timeStr) => {
			if (!timeStr) return null;
			const parts = timeStr.split(':');
			if (parts.length < 2) return null;
			return { hours: parseInt(parts[0]), minutes: parseInt(parts[1]) };
		};

		// Check Start
		if (startDateStr) {
			const startDate = parseDate(startDateStr);
			if (startDate) {
				if (startTimeStr) {
					const startTime = parseTime(startTimeStr);
					if (startTime) {
						startDate.setHours(startTime.hours, startTime.minutes, 0, 0);
					} else {
						startDate.setHours(0, 0, 0, 0);
					}
				} else {
					startDate.setHours(0, 0, 0, 0);
				}
				
				if (now < startDate) return false;
			}
		}
		
		// Check End
		if (endDateStr) {
			const endDate = parseDate(endDateStr);
			if (endDate) {
				if (endTimeStr) {
					const endTime = parseTime(endTimeStr);
					if (endTime) {
						endDate.setHours(endTime.hours, endTime.minutes, 59, 999);
					} else {
						endDate.setHours(23, 59, 59, 999);
					}
				} else {
					endDate.setHours(23, 59, 59, 999);
				}

				if (now > endDate) return false;
			}
		}

		return true;
	}
	
	lazyLoadImagesInElement(element) {
		// Find all images with data-src attribute
		const lazyImages = element.querySelectorAll('img[data-src]');
		lazyImages.forEach(img => {
			if (img.dataset.src) {
				img.src = img.dataset.src;
				img.removeAttribute('data-src');
			}
		});
		
		// Find all elements with data-bg attribute for background images
		const lazyBackgrounds = element.querySelectorAll('[data-bg]');
		lazyBackgrounds.forEach(el => {
			if (el.dataset.bg) {
				el.style.backgroundImage = `url(${el.dataset.bg})`;
				el.removeAttribute('data-bg');
			}
		});
	}

	disconnectedCallback() {
		if (this.splide) {
			this.splide.destroy();
		}
		window.removeEventListener('resize', this.handleResize.bind(this));
	}

	handleResize() {
		const enableDesktopCarousel = this.getAttribute('enable-desktop-carousel') === 'true';
		const enableMobileCarousel = this.getAttribute('enable-mobile-carousel') === 'true';
		const isMobile = window.innerWidth < 750;

		// Determine if we need to reinitialize
		const shouldBeCarousel = (isMobile && enableMobileCarousel) || (!isMobile && enableDesktopCarousel);
		const isCurrentlyCarousel = this.splide !== null;

		if (shouldBeCarousel !== isCurrentlyCarousel) {
			// Reload the page to reinitialize
			window.location.reload();
		}
	}

	initSplide(deviceSpecificOptions = {}) {
		// Base options
		const options = {
			type: 'slide',
			arrows: true,
			pagination: true,
			autoplay: false,
			interval: 5000,
			speed: 400,
			perMove: 1,
			...deviceSpecificOptions,
		};

		// Initialize Splide
		this.classList.add('splide');
		this.splide = new Splide(this, options);
		
		// Enhanced lazy loading for deferred images
		this.splide.on('mounted', () => {
			this.loadDeferredImages();
			this.fixQuickBuyModals();
		});
		
		this.splide.on('move', () => {
			this.loadDeferredImages();
		});
		
		// Preload next slide image on autoplay
		if (options.autoplay) {
			this.splide.on('autoplay:playing', () => {
				this.preloadNextSlideImage();
			});
		}
		
		this.splide.mount();
	}

	loadDeferredImages() {
		if (!this.splide) return;
		
		const activeIndex = this.splide.index;
		const slides = this.querySelectorAll('.splide__slide');
		
		slides.forEach((slide, index) => {
			// Load current slide and adjacent slides
			if (Math.abs(index - activeIndex) <= 1) {
				this.loadSlideImages(slide);
			}
		});
	}

	preloadNextSlideImage() {
		if (!this.splide) return;
		
		const nextIndex = (this.splide.index + 1) % this.splide.length;
		const nextSlide = this.querySelectorAll('.splide__slide')[nextIndex];
		
		if (nextSlide) {
			this.loadSlideImages(nextSlide);
		}
	}

	loadSlideImages(slide) {
		const deferredImages = slide.querySelectorAll('.deferred-image');
		
		deferredImages.forEach(img => {
			if (!img.src && img.dataset.desktopSrc) {
				const isMobile = window.innerWidth < 750;
				const srcToUse = isMobile ? 
					(img.dataset.mobileSrc || img.dataset.desktopSrc) : 
					img.dataset.desktopSrc;
				
				img.src = srcToUse;
				img.style.opacity = '1';
				img.classList.remove('deferred-image');
				
				// Remove data attributes to prevent reloading
				delete img.dataset.desktopSrc;
				delete img.dataset.mobileSrc;
			}
		});
		
		// Also handle existing lazy loading
		this.lazyLoadImagesInElement(slide);
	}

	lazyLoadInactiveSlides() {
		if (!this.splide) return;
		
		const slides = this.querySelectorAll('.splide__slide');
		const activeIndex = this.splide.index;
		
		slides.forEach((slide, index) => {
			if (Math.abs(index - activeIndex) > 1) { // Only load adjacent slides
				this.lazyLoadImagesInElement(slide);
			}
		});
	}

	fixQuickBuyModals() {
		// Get all slides
		const slides = this.querySelectorAll('.splide__slide');
		
		// Process each slide
		slides.forEach(slide => {
			// Get the slide ID (e.g., "splide04-slide01")
			const slideId = slide.id;
			
			// Find quick buy button and modal in this slide
			const quickBuyButton = slide.querySelector('.quick-buy-button');
			const quickBuyModal = slide.querySelector('.quick-buy-modal');
			
			if (quickBuyButton && quickBuyModal) {
				// Get the original IDs
				const originalModalId = quickBuyModal.id;
				
				// Create a new unique ID using the slide ID
				const newModalId = `${originalModalId}-${slideId}`;
				
				// Update the modal ID
				quickBuyModal.id = newModalId;
				
				// Update the button's aria-controls attribute
				quickBuyButton.setAttribute('aria-controls', newModalId);
			}
		});
	}

	static reinitializeAll() {
		document.querySelectorAll('custom-carousel').forEach(carousel => {
			if (carousel.splide) {
				carousel.splide.destroy();
			}
			carousel.connectedCallback();
		});
	}
}

// Register the custom element
customElements.define('custom-carousel', CustomCarousel);

class ProductListTabs extends HTMLElement {
	constructor() {
		super();
		this.tabs = [];
		this.tabContents = [];
		this.transitionDelay = 150;
		this.initializedTabs = new Set();
		this.modalInitialized = false;
	}

	handleTimeBoundTabs() {
		const now = new Date();
		const tabLinks = this.querySelectorAll('.custom-tab-link');
		
		tabLinks.forEach(tab => {
			const enableTimeBound = tab.getAttribute('data-enable-time-bound') === 'true';
			if (!enableTimeBound) return;
			
			const startTimeStr = tab.getAttribute('data-start-time');
			const endTimeStr = tab.getAttribute('data-end-time');
			const tabId = tab.getAttribute('data-tab');
			const content = this.querySelector(`.custom-tab-content[data-tab="${tabId}"]`);
			
			let isVisible = true;
			
			if (startTimeStr) {
				// Handle "YYYY-MM-DD HH:mm" format by replacing space with T for ISO compatibility
				const startDate = new Date(startTimeStr.replace(' ', 'T'));
				if (!isNaN(startDate.getTime()) && now < startDate) {
					isVisible = false;
				}
			}
			
			if (isVisible && endTimeStr) {
				const endDate = new Date(endTimeStr.replace(' ', 'T'));
				if (!isNaN(endDate.getTime()) && now > endDate) {
					isVisible = false;
				}
			}
			
			if (!isVisible) {
				tab.remove();
				if (content) content.remove();
			}
		});
	}

	connectedCallback() {
		this.tabsContainer = this.querySelector('.custom-tabs');
		this.contentContainer = this.querySelector('.product-tabs-content');
		
		// Handle time-bound visibility before initializing tabs
		this.handleTimeBoundTabs();
		
		// Set up tabs
		this.tabs = Array.from(this.querySelectorAll('.custom-tab-link'));
		this.tabContents = Array.from(this.querySelectorAll('.custom-tab-content'));
		
		// Check if this is inside a modal
		const isInModal = this.closest('.modal') !== null;
		
		if (isInModal) {
			// For modals, delay initialization until modal is opened
			this.setupModalTabsLater();
		} else {
			// For regular page sections, initialize immediately
			this.setupTabs();
		}
		
		// Set first tab as active by default
		if (this.tabs.length > 0) {
			this.activateTab(this.tabs[0], true);
		}
		
		// Add click event listeners to tabs
		this.tabs.forEach(tab => {
			tab.addEventListener('click', () => {
				this.activateTab(tab);
			});
		});
	}
	
	setupModalTabsLater() {
		// Find the modal element
		const modal = this.closest('.modal');
		if (!modal) return;
		
		// Wait for modal to be shown
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
					if (modal.hasAttribute('open') && !this.modalInitialized) {
						// Modal is now open, initialize tabs
						setTimeout(() => {
							this.setupTabs();
							this.modalInitialized = true;
						}, 100); // Small delay to ensure content is rendered
						observer.disconnect();
					}
				}
			});
		});
		
		observer.observe(modal, { attributes: true });
		
		// Fallback: if modal is already open when this runs
		if (modal.hasAttribute('open') && !this.modalInitialized) {
			setTimeout(() => {
				this.setupTabs();
				this.modalInitialized = true;
			}, 100);
		}
	}
	
	setupTabs() {
		// Store the HTML content of each tab except the first one,
		// but keep the button structure
		this.tabContents.forEach((content, index) => {
			if (index !== 0) { // Skip the first tab
				// Find the product grid and only store/clear that part
				const productGrid = content.querySelector('.product-grid');
				if (productGrid) {
					// Store only the product grid content
					content.dataset.originalGridContent = productGrid.innerHTML;
					productGrid.innerHTML = ''; // Clear only the grid content
				}
				content.style.display = 'none';
			}
			// Set opacity for all tabs
			content.style.opacity = index === 0 ? '1' : '0';
		});
	}
	
	activateTab(selectedTab, isInitialLoad = false) {
		// Don't do anything if this tab is already active
		if (selectedTab.classList.contains('current')) {
			return;
		}
		
		// Update tab states
		this.tabs.forEach(tab => {
			tab.classList.toggle('current', tab === selectedTab);
		});
		
		// Get the currently active content
		const currentContent = this.querySelector('.custom-tab-content.current');
		
		// Get the new content to show
		const tabId = selectedTab.getAttribute('data-tab');
		const newContent = this.querySelector(`.custom-tab-content[data-tab="${tabId}"]`);
		
		if (currentContent) {
			// Fade out current content
			currentContent.style.opacity = '0';
			
			// After transition, hide current and show new
			setTimeout(() => {
				currentContent.classList.remove('current');
				currentContent.style.display = 'none';
				
				this.loadTabContent(newContent);
				newContent.classList.add('current');
				newContent.style.display = 'block';
				
				// Trigger reflow to ensure transition happens
				void newContent.offsetWidth;
				
				// Fade in new content
				newContent.style.opacity = '1';
				
				// Reset the grid view for the new tab
				this.resetGridView(newContent);
				
				// Dispatch Swym event for tab change
				document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
				
				// Reinitialize carousels in the newly visible tab
				if (window.CustomCarousel) {
					setTimeout(() => {
						CustomCarousel.reinitializeAll();
					}, 100);
				}
			}, this.transitionDelay);
		} else {
			// No current content, just show the new one
			this.loadTabContent(newContent);
			newContent.classList.add('current');
			newContent.style.display = 'block';
			newContent.style.opacity = '1';
			
			// Reset the grid view for the new tab
			this.resetGridView(newContent);
			
			// Dispatch Swym event for tab change
			document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
			
			// Reinitialize carousels
			if (window.CustomCarousel) {
				setTimeout(() => {
					CustomCarousel.reinitializeAll();
				}, 100);
			}
		}
	}
	
	loadTabContent(tabContent) {
		// Only load content if it hasn't been loaded before and isn't the first tab
		if (!this.initializedTabs.has(tabContent) && tabContent !== this.tabContents[0]) {
			// Find the product grid and only restore that part
			const productGrid = tabContent.querySelector('.product-grid');
			if (productGrid && typeof tabContent.dataset.originalGridContent !== 'undefined') {
				// Restore only the grid content
				productGrid.innerHTML = tabContent.dataset.originalGridContent;
			}
			
			// Initialize any carousels in this content
			const carousels = tabContent.querySelectorAll('custom-carousel');
			carousels.forEach(carousel => {
				if (carousel.connectedCallback) {
					carousel.connectedCallback();
				}
			});
			
			// Mark this tab as initialized
			this.initializedTabs.add(tabContent);
		}
	}
	
	resetGridView(tabContent) {
		// Find the carousel in this tab content
		const carousel = tabContent.querySelector('custom-carousel');
		if (!carousel) return;
		
		// Only proceed if this is a grid view (not a carousel)
		if (carousel.splide) return;
		
		// Find the product grid
		const productGrid = carousel.querySelector('.product-grid');
		if (!productGrid) return;
		
		// Find the view more button container
		const buttonContainer = tabContent.querySelector('.grid-view-buttons');
		
		// Find the "View All" link
		const viewAllContainer = tabContent.querySelector('.product-tabs-content-bottom');
		
		// Get the columns per row
		const columnsPerRow = parseInt(carousel.getAttribute('desktop-columns')) || 3;
		const isMobile = window.innerWidth < 750;
		const actualColumns = isMobile ? 
			(parseInt(carousel.getAttribute('mobile-columns')) || 2) : 
			columnsPerRow;
		
		// Get all items in the grid
		const items = Array.from(productGrid.children);
		
		// If we have stored all items in the carousel
		if (carousel.allItems && carousel.allItems.length > 0) {
			// Clear the current grid
			while (productGrid.firstChild) {
				productGrid.removeChild(productGrid.firstChild);
			}
			
			// Add only the initial rows
			const initialRowsToShow = carousel.initialRowsToShow || 2;
			for (let i = 0; i < initialRowsToShow * actualColumns && i < carousel.allItems.length; i++) {
				const itemToAdd = carousel.allItems[i].cloneNode(true);
				productGrid.appendChild(itemToAdd);
			}
			
			// Update the current rows shown
			carousel.currentlyShownRows = initialRowsToShow;
			
			// Remove existing button container if it exists
			if (buttonContainer) {
				buttonContainer.remove();
			}
			
			// Create new button container
			const newButtonContainer = document.createElement('div');
			newButtonContainer.className = 'grid-view-buttons';
			newButtonContainer.style.textAlign = 'center';
			newButtonContainer.style.marginTop = '20px';
			productGrid.parentNode.insertBefore(newButtonContainer, productGrid.nextSibling);
			
			// Create new view more button if needed
			if (carousel.totalRows > initialRowsToShow) {
				carousel.createViewMoreButton(newButtonContainer);
				
				// Hide the View All button
				if (viewAllContainer) {
					viewAllContainer.style.display = 'none';
				}
			}
		}
	}
}

customElements.define('product-list-tabs', ProductListTabs);